import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-voiceagent');

/**
 * Debug Helper Utilities
 * 
 * These utilities help filter console logs in the browser for easier debugging.
 * You can run these functions in the browser console to filter logs.
 */

// Store original console methods
const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error
};

// Filter state
let currentFilter: string | null = null;

/**
 * Filter console logs to show only text-related messages
 * Usage in browser console: window.debugFilter.textOnly()
 */
export function filterTextOnly() {
  currentFilter = 'text';
  logger.info('🔤 TEXT FILTER: Now showing only text-related logs');
  
  console.log = (...args) => {
    const message = args.join(' ');
    if (message.includes('📝 TEXT') || message.includes('🔤 TEXT') || 
        message.includes('🚀 CONTENT_START: TEXT') || message.includes('🏁 CONTENT_END: TEXT') ||
        message.includes('ERROR') || message.includes('SYSTEM')) {
      originalConsole.log(...args);
    }
  };
  
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
}

/**
 * Filter console logs to show only audio-related messages
 * Usage in browser console: window.debugFilter.audioOnly()
 */
export function filterAudioOnly() {
  currentFilter = 'audio';
  logger.info('🎵 AUDIO FILTER: Now showing only audio-related logs');
  
  console.log = (...args) => {
    const message = args.join(' ');
    if (message.includes('🎵 AUDIO') || message.includes('📦 AUDIO_PACKET') || 
        message.includes('🚀 CONTENT_START: AUDIO') || message.includes('🏁 CONTENT_END: AUDIO') ||
        message.includes('🎵 AudioPlayer') || message.includes('ERROR')) {
      originalConsole.log(...args);
    }
  };
  
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
}

/**
 * Filter console logs to show only WebSocket-related messages
 * Usage in browser console: window.debugFilter.websocketOnly()
 */
export function filterWebSocketOnly() {
  currentFilter = 'websocket';
  logger.info('🔌 WEBSOCKET FILTER: Now showing only WebSocket-related logs');
  
  console.log = (...args) => {
    const message = args.join(' ');
    if (message.includes('🔌 WS') || message.includes('WebSocket') || 
        message.includes('serverReady') || message.includes('callFlowStarted') ||
        message.includes('ERROR')) {
      originalConsole.log(...args);
    }
  };
  
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
}

/**
 * Show only message flow events (contentStart, contentEnd, textOutput)
 * Usage in browser console: window.debugFilter.messageFlowOnly()
 */
export function filterMessageFlowOnly() {
  currentFilter = 'messageflow';
  logger.info('📋 MESSAGE FLOW FILTER: Now showing only message flow events');
  
  console.log = (...args) => {
    const message = args.join(' ');
    if (message.includes('📝 TEXT_MESSAGE') || message.includes('🔤 TEXT_EVENT') ||
        message.includes('🚀 CONTENT_START') || message.includes('🏁 CONTENT_END') ||
        message.includes('streamComplete') || message.includes('ERROR')) {
      originalConsole.log(...args);
    }
  };
  
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
}

/**
 * Restore original console logging (show all logs)
 * Usage in browser console: window.debugFilter.showAll()
 */
export function showAll() {
  currentFilter = null;
  console.log = originalConsole.log;
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
  logger.info('🔓 FILTER REMOVED: Now showing all console logs');
}

/**
 * Show current filter status
 * Usage in browser console: window.debugFilter.status()
 */
export function getFilterStatus() {
  logger.info(`📊 Current filter: ${currentFilter || 'none (showing all logs)'}`);
  return currentFilter;
}

// Export all functions as a single object for easy browser access
export const debugFilter = {
  textOnly: filterTextOnly,
  audioOnly: filterAudioOnly,
  websocketOnly: filterWebSocketOnly,
  messageFlowOnly: filterMessageFlowOnly,
  showAll: showAll,
  status: getFilterStatus
};

// Make it available globally in development
if (typeof window !== 'undefined') {
  (window as any).debugFilter = debugFilter;
  logger.info('🔧 Debug filters available at window.debugFilter');
  logger.info('Available methods: textOnly(), audioOnly(), websocketOnly(), messageFlowOnly(), showAll(), status()');
} 